import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, FormControlLabel, List, ListItemButton, ListItemText, Pagination, Paper, Switch, TextField, Typography } from '@mui/material';
import { Mic as MicIcon, Stop as StopIcon, Replay as RetryIcon, CheckCircle as DoneIcon, Delete as DeleteIcon, PlayArrow as PlayIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { audioService } from '../../services/api';
import { audioUrl } from '../../components/PlayWordButton';

const PAGE = 30;
const MAX_SECONDS = 8;

// Format que le navigateur sait enregistrer (Chrome/Firefox : webm ; Safari : mp4).
function pickMime() {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
  return candidates.find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || '';
}

function AudioRecorder() {
  const { enqueueSnackbar } = useSnackbar();
  const [q, setQ] = useState('');
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [page, setPage] = useState(1);
  const [list, setList] = useState({ entries: [], total: 0, recorded: 0 });
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(null);

  const [state, setState] = useState('idle'); // idle | recording | review | saving
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState('');
  const recorder = useRef(null);
  const chunks = useRef([]);
  const stream = useRef(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await audioService.worklist({ q: q.trim(), page, limit: PAGE, only_missing: onlyMissing ? 1 : 0 });
      setList(res.data);
    } catch (e) {
      enqueueSnackbar(e.response?.data?.error || 'Chargement impossible', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [q, page, onlyMissing, enqueueSnackbar]);

  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const stopTracks = () => { if (stream.current) stream.current.getTracks().forEach((t) => t.stop()); stream.current = null; };
  useEffect(() => () => { stopTracks(); clearInterval(timer.current); }, []);

  const reset = () => { setBlob(null); setPreviewUrl(''); setState('idle'); setSeconds(0); setError(''); };
  const select = (entry) => { reset(); setCurrent(entry); };

  const start = async () => {
    setError('');
    if (!navigator.mediaDevices || !window.MediaRecorder) { setError("Ce navigateur ne sait pas enregistrer. Essayez Chrome ou Firefox."); return; }
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    } catch {
      setError("Accès au micro refusé. Autorisez le micro dans le navigateur (le site doit être en https).");
      return;
    }
    const mimeType = pickMime();
    chunks.current = [];
    const rec = new MediaRecorder(stream.current, mimeType ? { mimeType } : undefined);
    recorder.current = rec;
    rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.current.push(e.data); };
    rec.onstop = () => {
      const type = rec.mimeType || mimeType || 'audio/webm';
      const b = new Blob(chunks.current, { type });
      stopTracks();
      clearInterval(timer.current);
      if (b.size < 400) { setError("Enregistrement vide. Parlez plus près du micro."); setState('idle'); return; }
      setBlob(b);
      setPreviewUrl(URL.createObjectURL(b));
      setState('review');
    };
    rec.start();
    setSeconds(0);
    setState('recording');
    timer.current = setInterval(() => {
      setSeconds((s) => {
        if (s + 1 >= MAX_SECONDS && rec.state === 'recording') rec.stop();
        return s + 1;
      });
    }, 1000);
  };
  const stop = () => { if (recorder.current && recorder.current.state === 'recording') recorder.current.stop(); };

  const nextMissing = () => {
    const idx = list.entries.findIndex((e) => e.letter === current.letter && e.duala === current.duala);
    const after = list.entries.slice(idx + 1).find((e) => !e.audio);
    return after || null;
  };

  const save = async () => {
    setState('saving');
    try {
      await audioService.upload(current.letter, current.duala, blob);
      enqueueSnackbar('Enregistrement validé', { variant: 'success' });
      const next = nextMissing();
      await load();
      reset();
      setCurrent(next);
    } catch (e) {
      setState('review');
      setError(e.response?.data?.error || "Envoi impossible");
    }
  };

  const remove = async () => {
    if (!current.audio_id || !window.confirm('Supprimer cet enregistrement ?')) return;
    try {
      await audioService.remove(current.audio_id);
      enqueueSnackbar('Enregistrement supprimé', { variant: 'info' });
      await load();
      setCurrent({ ...current, audio: null, audio_id: null });
    } catch (e) {
      enqueueSnackbar(e.response?.data?.error || 'Suppression impossible', { variant: 'error' });
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>Enregistrement des prononciations</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {list.recorded} mot(s) déjà enregistré(s). Choisissez un mot, appuyez sur « Enregistrer », dites le mot, puis validez.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>
        <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, width: '100%', p: 1.5 }}>
          <TextField size="small" fullWidth label="Chercher un mot (début du mot)" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
          <FormControlLabel sx={{ mt: 0.5 }} control={<Switch checked={onlyMissing} onChange={(e) => { setOnlyMissing(e.target.checked); setPage(1); }} />} label="Seulement les mots sans audio" />
          {loading ? <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box> : (
            <List dense disablePadding>
              {list.entries.map((e) => (
                <ListItemButton key={`${e.letter}|${e.duala}`} selected={current && current.letter === e.letter && current.duala === e.duala} onClick={() => select(e)}>
                  <ListItemText primary={e.duala} secondary={e.french} secondaryTypographyProps={{ noWrap: true }} />
                  {e.audio && <DoneIcon fontSize="small" color="success" />}
                </ListItemButton>
              ))}
              {list.entries.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>Aucun mot</Typography>}
            </List>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Pagination size="small" count={Math.max(1, Math.ceil(list.total / PAGE))} page={page} onChange={(_, p) => setPage(p)} />
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, width: '100%', p: 2.5, textAlign: 'center', position: { md: 'sticky' }, top: 80 }}>
          {!current ? (
            <Typography color="text.secondary">Choisissez un mot dans la liste.</Typography>
          ) : (
            <>
              <Typography variant="h3" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>{current.duala}</Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>{current.french}</Typography>

              {error && <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>{error}</Alert>}

              {state === 'idle' && (
                <Button variant="contained" size="large" startIcon={<MicIcon />} onClick={start} sx={{ textTransform: 'none', fontWeight: 700 }}>
                  {current.audio ? 'Enregistrer à nouveau' : 'Enregistrer'}
                </Button>
              )}
              {state === 'recording' && (
                <Box>
                  <Chip color="error" label={`Enregistrement… ${seconds} s`} sx={{ mb: 2 }} />
                  <Box><Button variant="contained" color="error" size="large" startIcon={<StopIcon />} onClick={stop} sx={{ textTransform: 'none', fontWeight: 700 }}>Arrêter</Button></Box>
                </Box>
              )}
              {(state === 'review' || state === 'saving') && (
                <Box>
                  <audio src={previewUrl} controls style={{ width: '100%', marginBottom: 12 }} />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Button variant="outlined" startIcon={<RetryIcon />} disabled={state === 'saving'} onClick={reset} sx={{ textTransform: 'none' }}>Recommencer</Button>
                    <Button variant="contained" color="success" startIcon={<DoneIcon />} disabled={state === 'saving'} onClick={save} sx={{ textTransform: 'none', fontWeight: 700 }}>
                      {state === 'saving' ? 'Envoi…' : 'Valider et envoyer'}
                    </Button>
                  </Box>
                </Box>
              )}

              {current.audio && state === 'idle' && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="caption" color="text.secondary">Enregistrement actuel</Typography>
                  <audio src={audioUrl(current.audio)} controls style={{ width: '100%', display: 'block', marginTop: 4 }} />
                  <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={remove} sx={{ mt: 1, textTransform: 'none' }}>Supprimer</Button>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
}

export default AudioRecorder;
